/**
 * Packaging OS — Initial App Shell (Phase 1O-A).
 *
 * Authenticated workspace placeholder. Real catalog / workflow surfaces
 * open in Phase 1O-B. This page:
 *   - Calls /api/whoami once on mount.
 *   - Branches on the API outcome (ok / auth-missing / auth-invalid /
 *     permission-denied / rate-limited / service-unavailable / network).
 *   - Renders Locked-card placeholders for the not-yet-built sections.
 *   - Surfaces the Request-Id from the response in the footer.
 *
 * No DB writes. No live AI. No fake activity.
 */

import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { getWhoAmI, type ApiResult, type WhoAmI } from "../api/client";
import { Button } from "../components/Button";
import { Footer } from "../components/Footer";
import { PublicHeader } from "../components/PublicHeader";
import { StatusPill } from "../components/StatusPill";
import { TrustBanner } from "../components/TrustBanner";
import { accessModeLabel } from "../i18n/accessMode";
import { strings, type UiStrings } from "../i18n/strings";
import { studioStrings } from "../i18n/studioStrings";
import { useLocale } from "../i18n/useLocale";
import { useLocaleSwitch } from "../i18n/useLocaleSwitch";

type LoadState =
  | { kind: "loading" }
  | { kind: "loaded"; result: ApiResult<WhoAmI> };

export function AppShellPage(): JSX.Element {
  const [locale] = useLocale();
  const setLocale = useLocaleSwitch();
  const t = strings(locale);
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const result = await getWhoAmI();
      if (!cancelled) setState({ kind: "loaded", result });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="po-page">
      <PublicHeader
        locale={locale}
        onLocaleChange={setLocale}
        switchLanguageLabel={t.switchLanguageLabel}
        brand={t.appName}
        navLinks={[{ to: "/", label: t.ctaBackToLanding }]}
      />

      <main className="po-page-main po-page-main--app">
        <TrustBanner message={t.trustBannerFull} />

        <header className="po-section-header">
          <span className="po-section-header__eyebrow">{t.appShellTitle}</span>
          <h1 className="po-section-header__title">{t.appShellTitle}</h1>
          <p className="po-note">{t.appShellSubtitle}</p>
          <p className="po-disclaimer" data-testid="app-honesty">
            {studioStrings(locale).internalWorkflowOnly}
          </p>
        </header>

        <div className="po-app-shell">
          <aside className="po-app-sidebar" aria-label="navigation">
            <span className="po-app-sidebar__group-label">
              {t.appShellNavCatalog}
            </span>
            <NavLink
              to="/app/catalog/brands"
              className="po-app-sidebar__item"
            >
              {t.catalogBrandsTitle}
            </NavLink>
            <NavLink
              to="/app/catalog/products"
              className="po-app-sidebar__item"
            >
              {t.catalogProductsTitle}
            </NavLink>
            <NavLink
              to="/app/catalog/packaging-projects"
              className="po-app-sidebar__item"
            >
              {t.catalogProjectsTitle}
            </NavLink>

            <span className="po-app-sidebar__group-label">
              {t.appShellNavWorkflows}
            </span>
            <NavLink
              to="/app/workflows/packaging-audit"
              className="po-app-sidebar__item"
            >
              {t.workflowDryRunTitle}
            </NavLink>
            <NavLink
              to="/app/workflows/product-brief"
              className="po-app-sidebar__item"
            >
              {t.intakeTitle}
            </NavLink>
            <NavLink
              to="/app/workflows/label-copy"
              className="po-app-sidebar__item"
            >
              {t.labelCopyTitle}
            </NavLink>
            <NavLink
              to="/app/workflows/material-structure"
              className="po-app-sidebar__item"
            >
              {t.materialTitle}
            </NavLink>
            <NavLink
              to="/app/workflows/sustainability-risk"
              className="po-app-sidebar__item"
            >
              {t.sustTitle}
            </NavLink>
            <NavLink
              to="/app/workflows/supplier-rfq"
              className="po-app-sidebar__item"
            >
              {t.supplierTitle}
            </NavLink>
            <NavLink
              to="/app/workflows/visual-direction"
              className="po-app-sidebar__item"
            >
              {t.visualTitle}
            </NavLink>
            <NavLink
              to="/app/workflows/packaging-scorecard"
              className="po-app-sidebar__item"
            >
              {t.scorecardTitle}
            </NavLink>
            <NavLink
              to="/app/workflows/launch-kit"
              className="po-app-sidebar__item"
            >
              {t.launchKitTitle}
            </NavLink>
            <NavLink
              to="/app/workflows/post-launch-analysis"
              className="po-app-sidebar__item"
            >
              {t.postLaunchTitle}
            </NavLink>
            <NavLink
              to="/app/workflows/review-checklist"
              className="po-app-sidebar__item"
            >
              {t.reviewChecklistTitle}
            </NavLink>

            <span className="po-app-sidebar__group-label">
              {t.appShellNavSettings}
            </span>
            <NavLink
              to="/app/prospects"
              className="po-app-sidebar__item"
            >
              Prospects · مشتریان
            </NavLink>
            <NavLink
              to="/app/projects"
              className="po-app-sidebar__item"
            >
              Projects · پروژه‌ها
            </NavLink>
            <NavLink
              to="/app/suppliers"
              className="po-app-sidebar__item"
            >
              Suppliers · تأمین‌کنندگان
            </NavLink>
            <NavLink
              to="/app/artifacts"
              className="po-app-sidebar__item"
            >
              {t.navArtifacts}
            </NavLink>
            <NavLink
              to="/app/knowledge"
              className="po-app-sidebar__item"
            >
              {t.navKnowledge}
            </NavLink>
            <NavLink
              to="/app/admin"
              className="po-app-sidebar__item"
            >
              {t.navAdmin}
            </NavLink>
            <NavLink to="/app" end className="po-app-sidebar__item">
              {t.appShellApiStatusHeading}
            </NavLink>
          </aside>

          <div className="po-stack">
            <section
              aria-labelledby="po-api-status-title"
              className="po-card"
            >
              <h2 id="po-api-status-title" className="po-card__title">
                {t.appShellApiStatusHeading}
              </h2>
              <ApiStatusBlock state={state} t={t} navigate={navigate} />
            </section>

            <section className="po-grid po-grid--2">
              <NavTile
                to="/app/catalog/brands"
                title={t.catalogBrandsTitle}
                body={t.catalogReadOnlyNotice}
              />
              <NavTile
                to="/app/catalog/products"
                title={t.catalogProductsTitle}
                body={t.catalogReadOnlyNotice}
              />
              <NavTile
                to="/app/catalog/packaging-projects"
                title={t.catalogProjectsTitle}
                body={t.catalogReadOnlyNotice}
              />
              <NavTile
                to="/app/workflows/packaging-audit"
                title={t.workflowDryRunTitle}
                body={t.workflowDryRunSubtitle}
              />
              <NavTile
                to="/app/workflows/product-brief"
                title={t.intakeTitle}
                body={t.intakeSubtitle}
              />
              <NavTile
                to="/app/workflows/label-copy"
                title={t.labelCopyTitle}
                body={t.labelCopySubtitle}
              />
              <NavTile
                to="/app/workflows/material-structure"
                title={t.materialTitle}
                body={t.materialSubtitle}
              />
              <NavTile
                to="/app/workflows/sustainability-risk"
                title={t.sustTitle}
                body={t.sustSubtitle}
              />
              <NavTile
                to="/app/workflows/supplier-rfq"
                title={t.supplierTitle}
                body={t.supplierSubtitle}
              />
              <NavTile
                to="/app/workflows/visual-direction"
                title={t.visualTitle}
                body={t.visualSubtitle}
              />
              <NavTile
                to="/app/workflows/packaging-scorecard"
                title={t.scorecardTitle}
                body={t.scorecardSubtitle}
              />
              <NavTile
                to="/app/workflows/launch-kit"
                title={t.launchKitTitle}
                body={t.launchKitSubtitle}
              />
              <NavTile
                to="/app/workflows/post-launch-analysis"
                title={t.postLaunchTitle}
                body={t.postLaunchSubtitle}
              />
              <NavTile
                to="/app/workflows/review-checklist"
                title={t.reviewChecklistTitle}
                body={t.reviewChecklistSubtitle}
              />
            </section>

            <p className="po-note po-note--muted po-note--small">
              {t.appShellWorkspacePending}
            </p>
          </div>
        </div>
      </main>

      <Footer copyright={t.copyrightLine} />
    </div>
  );
}

interface NavTileProps {
  to: string;
  title: string;
  body: string;
}

function NavTile({ to, title, body }: NavTileProps): JSX.Element {
  return (
    <Link to={to} className="po-card po-card--link">
      <h3 className="po-card__title">{title}</h3>
      <p className="po-card__body">{body}</p>
    </Link>
  );
}

interface ApiStatusBlockProps {
  state: LoadState;
  t: UiStrings;
  navigate: (path: string) => void;
}

function ApiStatusBlock({
  state,
  t,
  navigate,
}: ApiStatusBlockProps): JSX.Element {
  if (state.kind === "loading") {
    return (
      <div className="po-row">
        <StatusPill tone="info" label={t.whoAmIChecking} />
      </div>
    );
  }

  const r = state.result;
  switch (r.outcome) {
    case "ok": {
      const w = r.data!;
      return (
        <div className="po-stack">
          <div className="po-row">
            <StatusPill tone="ok" label={t.whoAmIOkLead} />
            <span className="dir-ltr-island po-note">{w.email}</span>
          </div>
          <p className="po-note">
            {t.whoAmIRoles}:{" "}
            <span className="dir-ltr-island">
              {w.role_codes.length > 0 ? w.role_codes.join(", ") : "—"}
            </span>
          </p>
          <p className="po-note">
            {t.whoAmIPermissionsCount}:{" "}
            <span className="po-request-id">{w.permissions_count}</span>
          </p>
          <p className="po-note po-note--small">
            {t.whoAmIAccessModeLabel}:{" "}
            <span data-testid="po-access-mode-label">
              {accessModeLabel(t, w.access_mode)}
            </span>
            {w.is_preview && (
              <>
                {" · "}
                <span>{t.whoAmIPreviewLabel}</span>
              </>
            )}
          </p>
          {r.requestId && (
            <p className="po-request-id">
              <span>{t.errRequestIdLabel} </span>
              <span className="dir-ltr-island">{r.requestId}</span>
            </p>
          )}
        </div>
      );
    }
    case "auth-missing":
    case "auth-invalid":
      return (
        <div className="po-stack">
          <div className="po-row">
            <StatusPill tone="warn" label={t.errAuthMissing} />
          </div>
          <p className="po-note">{t.errAuthInvalid}</p>
          <div className="po-row">
            <Button variant="primary" onClick={() => navigate("/login")}>
              {t.ctaSignInWithGoogle}
            </Button>
            <Button variant="ghost" onClick={() => navigate("/preview")}>
              {t.ctaRequestAccess}
            </Button>
          </div>
          {r.requestId && (
            <p className="po-request-id">
              <span>{t.errRequestIdLabel} </span>
              <span className="dir-ltr-island">{r.requestId}</span>
            </p>
          )}
        </div>
      );
    case "permission-denied":
      return (
        <div className="po-stack">
          <div className="po-row">
            <StatusPill tone="warn" label={t.errPermissionDenied} />
          </div>
          <Button variant="ghost" onClick={() => navigate("/preview")}>
            {t.ctaRequestAccess}
          </Button>
        </div>
      );
    case "rate-limited":
      return (
        <div className="po-row">
          <StatusPill tone="warn" label={t.errRateLimited} />
          {r.retryAfterSeconds && (
            <span className="po-request-id">
              ({r.retryAfterSeconds}s)
            </span>
          )}
        </div>
      );
    case "service-unavailable":
      return (
        <div className="po-row">
          <StatusPill tone="err" label={t.errServiceUnavailable} />
        </div>
      );
    case "network-error":
      return (
        <div className="po-row">
          <StatusPill tone="err" label={t.errNetwork} />
        </div>
      );
    default:
      return (
        <div className="po-row">
          <StatusPill tone="err" label={t.errUnknown} />
        </div>
      );
  }
}
